import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { countryName, flagEmoji } from "../i18n/countries";
import { filterCountryOptions, type CountryOption } from "./CountrySelect";

type Props = {
  /** Šifre koje se nude; spisak dolazi sa servera, jer se nude samo one koje nešto znače. */
  codes: string[];
  value: string[];
  onChange: (codes: string[]) => void;
  allLabel: string;
  ariaLabel?: string;
  style?: CSSProperties;
};

/**
 * Izbor više država odjednom, sa zastavama i pretragom.
 *
 * Kupci su u 45 država. Jedna po jedna znači da se poređenje „Nemačka prema Austriji" ne može
 * ni videti, a spisak od 45 redova bez pretrage se ne pregleda nego prelistava. Zato: više
 * izabranih odjednom, polje za pretragu iznad spiska i imena poređana po azbuci jezika korisnika,
 * a ne po šiframa — „Rusija" pre „Srbije", ne „RS" pre „RU".
 *
 * Izgled i ponašanje su isti kao kod {@link CountrySelect}; razlika je samo u tome što klik
 * dodaje ili sklanja državu i ne zatvara spisak, jer se bira više njih zaredom.
 */
export function CountryMultiSelect({ codes, value, onChange, allLabel, ariaLabel, style }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "sr";

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const options = useMemo<CountryOption[]>(
    () => codes
      .map((c) => ({ code: c, label: countryName(c, locale) }))
      .sort((a, b) => a.label.localeCompare(b.label, locale)),
    [codes, locale],
  );
  const filtered = useMemo(() => filterCountryOptions(options, query), [options, query]);
  // Red 0 je uvek „sve države"; 1..n su filtrirane.
  const rowCount = filtered.length + 1;
  const izabrano = useMemo(() => new Set(value), [value]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => { setActive(0); }, [query]);

  function prebaci(code: string) {
    onChange(izabrano.has(code) ? value.filter((c) => c !== code) : [...value, code]);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((a) => Math.min(rowCount - 1, a + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
        break;
      case "Enter": {
        e.preventDefault();
        if (active === 0) onChange([]);
        else {
          const o = filtered[active - 1];
          if (o) prebaci(o.code);
        }
        break;
      }
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
    }
  }

  // Jedna izabrana se ispisuje imenom; više njih brojem, jer se tri imena ne uklapaju u dugme.
  const naslov = value.length === 0
    ? allLabel
    : value.length === 1
      ? `${flagEmoji(value[0])} ${countryName(value[0], locale)}`.trim()
      : t("filter.countriesSelected", "{{count}} država", { count: value.length });

  return (
    <div ref={wrapRef} style={{ position: "relative", ...style }}>
      <button
        type="button"
        className="input"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        style={trigger}
      >
        <span style={{ ...triggerLabel, color: value.length ? "var(--ink)" : "var(--ink-3)" }}>
          {naslov}
        </span>
        <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden
          style={{ flex: "none", transform: open ? "rotate(180deg)" : "none", transition: "transform .12s", color: "var(--ink-3)" }}>
          <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M1 1l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div style={panel}>
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("action.search", "Pretraga") + "…"}
            aria-label={t("action.search", "Pretraga")}
            className="input"
            style={{ flex: "none" }}
          />
          <div role="listbox" aria-multiselectable aria-label={ariaLabel} className="dd-scroll" style={list}>
            <button
              type="button"
              role="option"
              aria-selected={value.length === 0}
              onMouseEnter={() => setActive(0)}
              onClick={() => onChange([])}
              style={{ ...option, background: active === 0 ? "var(--surface-2)" : "transparent",
                       fontWeight: value.length === 0 ? 600 : 400 }}
            >
              <span style={{ width: 20, flex: "none" }} />
              <span style={{ flex: 1, color: "var(--ink-3)" }}>{allLabel}</span>
            </button>

            {filtered.length === 0 ? (
              <div style={emptyMsg}>
                {t("search.noResults", { term: query, defaultValue: 'Nema rezultata za „{{term}}“' })}
              </div>
            ) : (
              filtered.map((o, i) => {
                const red = i + 1;
                const sel = izabrano.has(o.code);
                return (
                  <button
                    key={o.code}
                    type="button"
                    role="option"
                    aria-selected={sel}
                    onMouseEnter={() => setActive(red)}
                    onClick={() => prebaci(o.code)}
                    style={{ ...option, background: red === active ? "var(--surface-2)" : "transparent",
                             fontWeight: sel ? 600 : 400 }}
                  >
                    <span aria-hidden style={{ width: 20, flex: "none" }}>{flagEmoji(o.code)}</span>
                    <span style={{ flex: 1 }}>{o.label}</span>
                    {sel && (
                      <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden
                           style={{ flex: "none", color: "var(--brand-primary)" }}>
                        <path fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                              strokeLinejoin="round" d="M5 12l5 5L20 7" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const trigger: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, textAlign: "left", cursor: "pointer",
};
const triggerLabel: CSSProperties = {
  flex: 1, display: "flex", alignItems: "center", gap: 8,
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const panel: CSSProperties = {
  position: "absolute", zIndex: 30, top: "calc(100% + 4px)", left: 0, minWidth: "100%",
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)", boxShadow: "var(--shadow-2)",
  padding: 8, display: "flex", flexDirection: "column", gap: 6,
};
const list: CSSProperties = {
  maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2,
};
const option: CSSProperties = {
  display: "flex", gap: 10, alignItems: "center", width: "100%", textAlign: "left",
  padding: "8px 10px", borderRadius: 6, border: "none", cursor: "pointer",
  fontSize: 13, color: "var(--ink)",
};
const emptyMsg: CSSProperties = { padding: "10px 10px", fontSize: 12.5, color: "var(--ink-3)" };
